import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { getStoreCurrency } from '@/lib/formatters'
import { useState,useEffect } from "react"
import { createFileRoute,Outlet,useNavigate,useSearch } from "@tanstack/react-router"
import { useQuery,useMutation,useQueryClient } from '@tanstack/react-query'
import { z } from "zod"
import { api,type AxiosError,type MaybeEnveloped } from '@/lib/api-client'
import { getSaleConfigsByIds } from '@/features/catalog/api'
import { toast } from "sonner"
import { useOrganizationShop } from "@/hooks/use-organization-shop"
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Search,MoreHorizontal,XCircle,Replace,RefreshCw,CornerDownRight,Package,Shield,ShieldOff,X,Link,Scale,MapPin,Link2Off } from "lucide-react"
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import {
Tooltip,
TooltipContent,
TooltipProvider,
TooltipTrigger,
} from "@/components/ui/tooltip"
import {
HoverCard,
HoverCardContent,
HoverCardTrigger,
} from "@/components/ui/hover-card"
import { PageBreadcrumb } from "@/components/dashboard/page-breadcrumb"
import { InventoryEmptyState } from "@/components/dashboard/inventory-empty-state"
import { DataTablePagination } from "@/components/dashboard/data-table-pagination"
import { HoldAuditHoverCard } from "@/features/inventory/hold-audit-hover-card"
import { Skeleton } from "@/components/ui/skeleton"
import { SaleConfigListCompact } from "@/features/catalog/sale-config-list-compact"
import { SaleConfig } from "@/types"
import { formatDistanceToNow } from 'date-fns'
import { cn } from "@/lib/utils"
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { queryKeys } from '@/lib/query-keys'
import { extractArray } from '@/features/http'
import { FormDialog } from "@/components/form-dialog"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { formatDateOnly } from '@/lib/time'
const PackageSidePanel = React.lazy(() => import("@/features/inventory/package-side-panel").then(m => ({ default: m.PackageSidePanel })))
const BulkMapPackagesDialog = React.lazy(() => import("@/features/inventory/dialogs/bulk-map-packages-dialog").then(m => ({ default: m.BulkMapPackagesDialog })))
const AdjustPackageDialog = React.lazy(() => import("@/features/inventory/dialogs/adjust-package-dialog").then(m => ({ default: m.AdjustPackageDialog })))
const BulkAdjustPackagesDialog = React.lazy(() => import("@/features/inventory/dialogs/bulk-adjust-packages-dialog").then(m => ({ default: m.BulkAdjustPackagesDialog })))
const MovePackageDialog = React.lazy(() => import("@/features/inventory/dialogs/move-package-dialog").then(m => ({ default: m.MovePackageDialog })))
const FinishPackageDialog = React.lazy(() => import("@/features/inventory/dialogs/finish-package-dialog").then(m => ({ default: m.FinishPackageDialog })))
const BulkFinishPackagesDialog = React.lazy(() => import("@/features/inventory/dialogs/bulk-finish-packages-dialog").then(m => ({ default: m.BulkFinishPackagesDialog })))

export interface MetrcPackage {
  id: string
  shopId: string
  label: string
  packageType: string
  sourceHarvestNames: string
  sourcePackageLabels: string
  itemName: string
  itemCategoryName: string
  itemStrainName: string
  quantity: number
  unitOfMeasureName: string
  unitOfMeasureAbbr: string
  packagedDate: string
  useByDate?: string | null
  labTestingState: string
  labTestingStateDate: string
  isTestingSample: boolean
  isProcessValidationTestingSample: boolean
  productRequiresRemediation: boolean
  containsRemediatedProduct: boolean
  remediationDate: string | null
  receivedFromManifestNumber: string
  receivedFromFacilityLicenses: string
  receivedFromFacilityName: string
  receivedDateTime: string
  isOnHold: boolean
  archivedDate: string | null
  finishedDate: string | null
  status: string
  locationId?: string | null
  locationName?: string | null
  productId?: string | null
  saleConfigId?: string | null
  metrcItemId?: string | null
  createdAt: string
  updatedAt: string

  product?: {
    id: string
    name: string
    images?: string[]
  } | null
  saleConfig?: {
    id: string
    name: string
    sku: string
  } | null
  metrcItem?: {
    id: string
    name: string
    productId: string | null
    product?: { id: string; name: string } | null
  } | null
  unitOfMeasurement?: {
    id: string
    name: string
    shortForm: string
    slug: string
    measurementType: 'WEIGHT' | 'COUNT' | 'VOLUME'
  } | null
}

export const Route = createFileRoute('/_dashboard/inventory/packages')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: z.object({
    page: z.number().catch(1),
    limit: z.number().catch(20),
    q: z.string().catch(''),
  }),
  component: PackagesLayout,
})

let suppressedPackagesAutoOpenSignature: string | null = null

function PackagesLayout() {
  return <Outlet />
}

export function PackagesPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, q: searchQuery } = useSearch({ from: Route.id })
  const { shopId, isLoading: shopLoading } = useOrganizationShop()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [lastSynced, setLastSynced] = useState<Date | null>(null)

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery)

  const [selectedPackageIds, setSelectedPackageIds] = useState<Set<string>>(new Set())

  const setSelectedPackageIdForPanel = React.useCallback((id: string | null) => {
    if (id) {
      navigate({
        to: '/inventory/packages/$item',
        params: { item: id },
        search: (prev) => ({ ...prev }),
      })
      return
    }

    navigate({ to: '/inventory/packages', search: (prev) => ({ ...prev }) })
  }, [navigate])

  const [bulkMapOpen, setBulkMapOpen] = useState(false)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false)
  const [moveOpen, setMoveOpen] = useState(false)
  const [finishOpen, setFinishOpen] = useState(false)
  const [bulkFinishOpen, setBulkFinishOpen] = useState(false)
  const [bulkHoldOpen, setBulkHoldOpen] = useState(false)
  const [bulkHoldReason, setBulkHoldReason] = useState("")
  const [bulkReleaseOpen, setBulkReleaseOpen] = useState(false)
  const [bulkReleaseNote, setBulkReleaseNote] = useState("")
  const [isUnmapDialogOpen, setIsUnmapDialogOpen] = useState(false)
  const [actionPackage, setActionPackage] = useState<MetrcPackage | null>(null)

  const normalizeListResponse = (payload: unknown) => {
    if (Array.isArray(payload)) {
      return { data: payload, meta: null }
    }

    const p = payload as Record<string, unknown>
    if (!p) return { data: [], meta: null }

    if (Array.isArray(p.data)) {
      return { data: p.data, meta: p.meta || p.pagination || p.pageInfo || null }
    }

    if (Array.isArray(p.Data)) {
      return { data: p.Data, meta: p.Meta || p.meta || null }
    }

    if (Array.isArray(p.items)) {
      return { data: p.items, meta: p.meta || p.pagination || null }
    }

    if (Array.isArray(p.results)) {
      return { data: p.results, meta: p.meta || p.pagination || null }
    }

    const d = p.data as Record<string, unknown>
    if (d && Array.isArray(d.items)) {
      return { data: d.items, meta: d.meta || p.meta || p.pagination || null }
    }

    if (d && Array.isArray(d.data)) {
      return { data: d.data, meta: d.meta || p.meta || p.pagination || null }
    }

    return { data: [], meta: p.meta || p.pagination || null }
  }

  const packagesQuery = useQuery({
    queryKey: queryKeys.packages(shopId!, { page, limit, search: debouncedSearch }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page, limit, shopId }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await api.get(`/metrc/packages`, { params })
      const { data: normalizedData, meta } = normalizeListResponse(response)
      const fallbackData = extractArray<MetrcPackage>(response)
      const pData = (normalizedData.length > 0 ? normalizedData : fallbackData) as MetrcPackage[]

      const total = meta
        ? ((meta as Record<string, unknown>).total as number) ||
        ((meta as Record<string, unknown>).totalItems as number) ||
        ((meta as Record<string, unknown>).count as number) ||
        pData.length
        : pData.length
      const totalPagesCount = meta
        ? ((meta as Record<string, unknown>).totalPages as number) ||
        ((meta as Record<string, unknown>).pages as number) ||
        Math.ceil(total / limit) ||
        1
        : 1

      return { data: pData, meta: { total, totalPages: totalPagesCount } }
    },
    enabled: !!shopId && !shopLoading,
  })

  const packages = packagesQuery.data?.data ?? []
  const loading = packagesQuery.isLoading || shopLoading
  const totalItems = packagesQuery.data?.meta.total ?? 0
  const totalPages = packagesQuery.data?.meta.totalPages ?? 1
  const currentPageHasSelectedPackage = !!selectedId && packages.some((pkg) => pkg.id === selectedId)
  const effectiveSelectedPackageId = (() => {
    if (currentPageHasSelectedPackage) return selectedId
    if (loading || packages.length === 0) return undefined
    if (!selectedId && suppressedPackagesAutoOpenSignature === pageSignature) return undefined
    return packages[0].id
  })()

  const saleConfigIds = Array.from(
    new Set(packages.map((pkg) => pkg.saleConfigId).filter((id): id is string => !!id))
  )

  const saleConfigsQuery = useQuery({
    queryKey: queryKeys.saleConfigsByIds(saleConfigIds),
    queryFn: () => getSaleConfigsByIds(saleConfigIds),
    enabled: saleConfigIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const saleConfigDetails = saleConfigsQuery.data ?? {}

  const directPackageQuery = useQuery({
    queryKey: queryKeys.metrcPackage(effectiveSelectedPackageId ?? selectedId),
    queryFn: async () => {
      const res = await api.get<MaybeEnveloped<MetrcPackage>>(`/metrc/packages/${effectiveSelectedPackageId ?? selectedId}`)
      return (res?.data || res) as MetrcPackage
    },
    enabled: !!(effectiveSelectedPackageId ?? selectedId) && !packages.find(p => p.id === (effectiveSelectedPackageId ?? selectedId)),
  })

  const selectedPackage = packages.find(p => p.id === effectiveSelectedPackageId) ?? directPackageQuery.data ?? null

  const closeSelectedPackage = React.useCallback(() => {
    suppressedPackagesAutoOpenSignature = pageSignature
    setSelectedPackageIdForPanel(null)
  }, [pageSignature, setSelectedPackageIdForPanel])

  useEffect(() => {
    if (!effectiveSelectedPackageId || effectiveSelectedPackageId === selectedId) return
    setSelectedPackageIdForPanel(effectiveSelectedPackageId)
  }, [effectiveSelectedPackageId, selectedId, setSelectedPackageIdForPanel])

  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/inventory/packages')) {
          suppressedPackagesAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const syncMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/metrc/packages/sync`, null, { params: { shopId } })
    },
    onSuccess: () => {
      setLastSynced(new Date())
      queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
      toast.success("Sync complete", {
        description: "Successfully synced packages from Metrc.",
      })
    },
    onError: () => {
      toast.error("Sync failed", {
        description: "Could not sync packages. Please try again.",
      })
    },
  })

  const bulkHoldMutation = useMutation({
    mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
      const results = await Promise.allSettled(
        ids.map(id => api.post(`/metrc/packages/${id}/hold`, { reason }))
      )
      return results
    },
    onSuccess: (results) => {
      const failed = results.filter(r => r.status === 'rejected').length
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      if (failed > 0) {
        toast.error(`Hold placed on ${succeeded} package${succeeded !== 1 ? 's' : ''}`, {
          description: `${failed} package${failed !== 1 ? 's' : ''} could not be placed on hold (may already be on hold or inactive).`,
        })
      } else {
        toast.success('Hold placed', { description: `Successfully placed ${succeeded} package${succeeded !== 1 ? 's' : ''} on local hold.` })
      }
      setBulkHoldOpen(false)
      setBulkHoldReason('')
      setSelectedPackageIds(new Set())
      queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
    },
    onError: (error: unknown) => {
      toast.error('Failed to place hold', { description: (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? 'An unexpected error occurred.' })
    },
  })

  const bulkReleaseHoldMutation = useMutation({
    mutationFn: async ({ ids, note }: { ids: string[]; note?: string }) => {
      const results = await Promise.allSettled(
        ids.map(id => api.delete(`/metrc/packages/${id}/hold`, { data: note ? { note } : undefined }))
      )
      return results
    },
    onSuccess: (results) => {
      const failed = results.filter(r => r.status === 'rejected').length
      const succeeded = results.filter(r => r.status === 'fulfilled').length
      if (failed > 0) {
        toast.error(`Hold released on ${succeeded} package${succeeded !== 1 ? 's' : ''}`, {
          description: `${failed} package${failed !== 1 ? 's' : ''} could not be released (may not be on local hold).`,
        })
      } else {
        toast.success('Hold released', { description: `Successfully released hold on ${succeeded} package${succeeded !== 1 ? 's' : ''}.` })
      }
      setBulkReleaseOpen(false)
      setBulkReleaseNote('')
      setSelectedPackageIds(new Set())
      queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
    },
    onError: (error: unknown) => {
      toast.error('Failed to release hold', { description: (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? 'An unexpected error occurred.' })
    },
  })

  const unmapMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => api.delete(`/metrc/packages/${id}/map`)))
    },
    onSuccess: () => {
      toast.success("Packages unmapped", {
        description: `Successfully unmapped ${selectedPackageIds.size} packages.`
      })
      setSelectedPackageIds(new Set())
      queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
    },
    onError: (error: unknown) => {
      toast.error("Unmap Failed", {
        description: (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? "Could not unmap some packages. Please try again."
      })
    },
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearchQuery)
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.q ?? '') === localSearchQuery ? prev : { ...prev, q: localSearchQuery || undefined, page: 1 }) })
    }, 500)
    return () => clearTimeout(timer)
  }, [localSearchQuery, navigate])

  const toggleSelectAll = () => {
    if (selectedPackageIds.size === packages.length) {
      setSelectedPackageIds(new Set())
    } else {
      setSelectedPackageIds(new Set(packages.map(p => p.id)))
    }
  }

  const toggleSelectPackage = (id: string) => {
    const newSelected = new Set(selectedPackageIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedPackageIds(newSelected)
  }

  const handleSync = () => {
    if (!shopId || syncMutation.isPending) return
    syncMutation.mutate()
  }

  const formatConfigPrice = (config: Partial<SaleConfig> | null | undefined) => {
    if (!config) return null

    const uom = config.sellUom?.shortForm || (config.sellType === 'WEIGHT' ? 'g' : 'ea')
    const displayUom = uom === 'pc' ? 'ea' : uom
    const isExplicitlyTiered = config.pricingScheme?.mode === 'TIERED'

    if (isExplicitlyTiered) {
      const tiers = config.pricingScheme?.tiers || []
      if (tiers.length === 0) return null
      const minPrice = Math.min(...tiers.map((t: { unitPriceCents: number }) => t.unitPriceCents))
      const formattedPrice = new Intl.NumberFormat('en-US', { style: 'currency', currency: getStoreCurrency() }).format(minPrice / 100)
      return `From ${formattedPrice}/${displayUom}`
    }

    let priceCents = config.flatPriceCents ?? config.simplePriceCents ?? config.pricingScheme?.flatPriceCents
    if ((priceCents === undefined || priceCents === null) && config.pricingScheme?.tiers && config.pricingScheme.tiers.length > 0) {
      priceCents = config.pricingScheme.tiers[0].unitPriceCents
    }

    if (priceCents === undefined || priceCents === null) return null
    const price = new Intl.NumberFormat('en-US', { style: 'currency', currency: getStoreCurrency() }).format(priceCents / 100)
    return `${price}/${displayUom}`
  }

  return (
    <div className="space-y-6 h-full">
      <PageBreadcrumb />

      <div className="flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Metrc Packages</h2>
          <p className="text-muted-foreground mt-1 flex items-center gap-2">
            Cannabis inventory tracked through Metrc.
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <span className="text-muted-foreground">
            {lastSynced ? `Synced ${formatDistanceToNow(lastSynced)} ago` : 'Ready to sync'}
          </span>
          <div className={cn("h-1.5 w-1.5 rounded-full mr-2", lastSynced ? "bg-emerald-500" : "bg-muted-foreground")}></div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSync}
            disabled={syncMutation.isPending || !shopId}
          >
            <RefreshCw className={cn("mr-2 h-4 w-4", syncMutation.isPending && "animate-spin")} />
            {syncMutation.isPending ? "Syncing..." : "Sync Now"}
          </Button>
        </div>
      </div>

      {(packages.length > 0 || localSearchQuery || loading) && (
        <div className="flex items-center gap-2 mb-4">
          <div className="relative max-w-sm w-full group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by label, item, product..."
              className="pl-9 pr-8"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
            />
            {localSearchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLocalSearchQuery('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void packagesQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={cn(
          "flex-1 min-w-0 transition-all duration-300",
          !loading && packages.length === 0 ? "" : "border rounded-md squircle bg-card shadow-sm overflow-hidden",
          selectedPackage ? "w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]" : "w-full"
        )}>
          {!loading && packages.length === 0 ? (
            <InventoryEmptyState 
              title="No packages found"
              description="Packages will appear here once synced from Metrc."
            />
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-14rem)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[40px] px-4 rounded-tl-none border-l-0">
                        <Checkbox
                          checked={packages.length > 0 && selectedPackageIds.size === packages.length}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead className="w-[180px]">Tag Label</TableHead>
                      <TableHead>Item / Product</TableHead>
                      <TableHead className="w-[100px] text-right">Qty</TableHead>
                      <TableHead className={`w-[90px] text-center ${selectedPackage ? "hidden" : "hidden md:table-cell"}`}>Metrc Item</TableHead>
                      <TableHead className="w-[120px]">Packaged</TableHead>

                      <TableHead className="w-[50px] text-right rounded-tr-none border-r-0" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      /* Skeleton rows mirror the real row's SEVEN cells (incl. the
                         responsive-hidden Metrc Item column) — extra cells would
                         widen the table past the header and leave a white notch
                         in the header row. */
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="px-4 rounded-tl-none border-l-0">
                            <Skeleton className="h-4 w-4 rounded" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-24" />
                          </TableCell>
                          <TableCell>
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-20" />
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-12 ml-auto" />
                          </TableCell>
                          <TableCell className={`text-center ${selectedPackage ? "hidden" : "hidden md:table-cell"}`}>
                            <Skeleton className="h-6 w-16 mx-auto rounded-full" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-20" />
                          </TableCell>
                          <TableCell className="text-right rounded-tr-none border-r-0">
                            <Skeleton className="h-4 w-8 ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      packages.map((pkg) => {
                        const isMapped = !!pkg.saleConfigId
                        const isSelected = selectedPackageIds.has(pkg.id)

                        const mappedSaleConfig = pkg.saleConfigId ? saleConfigDetails[pkg.saleConfigId] : null
                        const saleConfigForPricing = mappedSaleConfig || pkg.saleConfig

                        return (
                          <TableRow
                            key={pkg.id}
                            className={`cursor-pointer transition-colors group hover:bg-muted/50 ${effectiveSelectedPackageId === pkg.id ? "bg-muted" : ""}`}
                            onClick={() => {
                              if (effectiveSelectedPackageId === pkg.id) {
                                closeSelectedPackage()
                                return
                              }
                              setSelectedPackageIdForPanel(pkg.id)
                            }}
                            data-state={isSelected ? "selected" : undefined}
                          >
                            <TableCell className="px-4 rounded-tl-none border-l-0" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelectPackage(pkg.id)}
                                aria-label={`Select package ${pkg.label}`}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              <TooltipProvider delayDuration={300}>
                                <span className="flex items-center gap-1.5">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="cursor-help font-mono inline-flex items-baseline border-b border-dashed border-muted-foreground/40 pb-0.5 transition-colors hover:border-muted-foreground/70">
                                        {pkg.label.substring(0, 5)}
                                        <span className="text-lg opacity-60 px-0.5 leading-none scale-110">...</span>
                                        {pkg.label.substring(pkg.label.length - 5)}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="font-mono">{pkg.label}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span
                                        className={`inline-block h-2 w-2 rounded-full cursor-help shrink-0 ${pkg.status === 'ACTIVE' ? 'bg-emerald-500' :
                                          pkg.status === 'ON_HOLD' ? 'bg-amber-500' :
                                            pkg.status === 'IN_TRANSIT' ? 'bg-blue-500' :
                                              'bg-muted-foreground'
                                          }`}
                                      />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{pkg.status === 'ACTIVE' ? 'Active' : pkg.status === 'ON_HOLD' ? 'On Hold' : pkg.status === 'IN_TRANSIT' ? 'In Transit' : pkg.status === 'INACTIVE' ? 'Inactive' : pkg.status}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </span>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <span className="font-medium">{pkg.itemName}</span>
                                {pkg.isOnHold && (
                                  <HoldAuditHoverCard packageId={pkg.id} />
                                )}
                              </div>
                              {isMapped ? (
                                <div className="flex flex-col text-sm mt-0.5 space-y-0.5">
                                  <div className="text-muted-foreground flex items-center">
                                    <Package className="w-3 h-3 mr-1" />
                                    <span className="truncate max-w-[200px]">{pkg.product?.name || 'Mapped Product'}</span>
                                  </div>
                                  <div className="text-muted-foreground flex items-center pl-2">
                                    <CornerDownRight className="w-3 h-3 mr-1" />
                                    {saleConfigForPricing ? (
                                      <HoverCard>
                                        <HoverCardTrigger asChild>
                                          <div className="inline-flex cursor-help">
                                            <Badge variant="secondary" className="font-mono text-xs">
                                              {formatConfigPrice(saleConfigForPricing) || "—"}
                                            </Badge>
                                          </div>
                                        </HoverCardTrigger>
                                        <HoverCardContent className="w-80 p-0" align="start">
                                          <div className="p-3 max-h-[300px] overflow-y-auto">
                                            <SaleConfigListCompact configs={[saleConfigForPricing as SaleConfig]} />
                                          </div>
                                        </HoverCardContent>
                                      </HoverCard>
                                    ) : (
                                      <span className="truncate max-w-[190px] font-medium text-foreground">Mapped Config</span>
                                    )}
                                    <TooltipProvider delayDuration={300}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 ml-1.5 shrink-0 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent><p>Mapped</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground italic flex items-center gap-1.5 mt-0.5">
                                  <Replace className="w-3 h-3 opacity-50" />
                                  (unmapped)
                                  <TooltipProvider delayDuration={300}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-block h-2 w-2 rounded-full border border-muted-foreground/50 shrink-0 cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent><p>Not mapped to a product</p></TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium font-mono">
                              {pkg.quantity}<span className="text-xs text-muted-foreground font-normal ml-0.5">{pkg.unitOfMeasureAbbr === 'ea' ? 'pc' : pkg.unitOfMeasureAbbr}</span>
                            </TableCell>
                            <TableCell className={`text-center ${selectedPackage ? "hidden" : "hidden md:table-cell"}`}>
                              {pkg.metrcItem ? (
                                pkg.metrcItem.productId ? (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center justify-center text-emerald-600 dark:text-emerald-500">
                                      <div className="h-2 w-2 rounded-full bg-emerald-500 mr-1.5"></div>
                                      <span className="text-xs">Mapped</span>
                                    </div>
                                    {pkg.metrcItem.name && (
                                      <span className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={pkg.metrcItem.name}>{pkg.metrcItem.name}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center justify-center text-amber-600 dark:text-amber-500">
                                      <div className="h-2 w-2 rounded-full bg-amber-400 mr-1.5"></div>
                                      <span className="text-xs">Unmapped</span>
                                    </div>
                                    {pkg.metrcItem.name && (
                                      <span className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={pkg.metrcItem.name}>{pkg.metrcItem.name}</span>
                                    )}
                                  </div>
                                )
                              ) : (
                                <div className="flex items-center justify-center text-muted-foreground">
                                  <div className="h-2 w-2 rounded-full bg-muted-foreground/40 mr-1.5"></div>
                                  <span className="text-xs">None</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs font-medium">
                              {pkg.packagedDate ? formatDateOnly(pkg.packagedDate, "MMM d, yyyy") : "N/A"}
                            </TableCell>

                            <TableCell className="text-right rounded-tr-none border-r-0">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"
                                  >
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-40">
                                  {pkg.isOnHold ? (
                                    <DropdownMenuItem className="gap-1.5" onClick={(e) => {
                                      e.stopPropagation()
                                      setActionPackage(pkg)
                                      setBulkReleaseOpen(true)
                                    }}>
                                      <ShieldOff className="h-4 w-4" />
                                      Release Hold
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem className="gap-1.5" onClick={(e) => {
                                      e.stopPropagation()
                                      setActionPackage(pkg)
                                      setBulkHoldOpen(true)
                                    }}>
                                      <Shield className="h-4 w-4" />
                                      Place on Hold
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem className="gap-1.5" onClick={(e) => {
                                    e.stopPropagation()
                                    setSelectedPackageIdForPanel(pkg.id)
                                    setAdjustOpen(true)
                                  }}>
                                    <Scale className="h-4 w-4" />
                                    Adjust Quantity
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="gap-1.5 text-red-600 focus:text-red-600"

                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setActionPackage(pkg)
                                      setFinishOpen(true)
                                    }}
                                  >
                                    <XCircle className="h-4 w-4" />
                                    Finish Package
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {packages.length > 0 && (
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  onPageChange={(p) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }) })}
                  onLimitChange={(l) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }) })}
                  entityName="packages"
                  isLoading={loading}
                >
                  {selectedPackageIds.size > 0 && (
                    <div className="flex items-center gap-2 mr-4 border-r pr-4 text-sm text-muted-foreground">
                      <span>Selected: {selectedPackageIds.size}</span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm">
                            Bulk Actions <MoreHorizontal className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-fit">
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap" onClick={() => setBulkMapOpen(true)}>
                            <Link className="h-4 w-4" />
                            Map to Product/SaleConfig
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap" onClick={() => setBulkAdjustOpen(true)}>
                            <Scale className="h-4 w-4" />
                            Adjust Quantity (Waste)
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/20" onClick={() => setBulkFinishOpen(true)}>
                            <XCircle className="h-4 w-4" />
                            Finish Packages
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap" onClick={() => setMoveOpen(true)}>
                            <MapPin className="h-4 w-4" />
                            Move to Location
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap" onClick={() => setBulkHoldOpen(true)}>
                            <Shield className="h-4 w-4" />
                            Place on Hold
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 whitespace-nowrap" onClick={() => setBulkReleaseOpen(true)}>
                            <ShieldOff className="h-4 w-4" />
                            Release Hold
                          </DropdownMenuItem>
                          <DropdownMenuItem className="gap-1.5 text-red-600 focus:text-red-600 whitespace-nowrap" onClick={() => setIsUnmapDialogOpen(true)}>
                            <Link2Off className="h-4 w-4" />
                            Unmap Selected
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </DataTablePagination>
              )}
            </>
          )}
        </div>

        {selectedPackage && (
          <div className="w-full md:w-[320px] lg:w-[360px] sticky top-0 shrink-0 mt-0 flex flex-col h-fit max-h-[calc(100vh-6rem)]">
            <React.Suspense fallback={null}>
              <PackageSidePanel
                packageItem={selectedPackage}
                shopId={shopId ?? ''}
                onRefresh={() => queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })}
                onClose={closeSelectedPackage}
              />
            </React.Suspense>
          </div>
        )}
      </div>

      <React.Suspense fallback={null}>
        <BulkMapPackagesDialog
          open={bulkMapOpen}
          onOpenChange={setBulkMapOpen}
          packages={packages.filter(p => selectedPackageIds.has(p.id))}
          onMapComplete={() => {
            setBulkMapOpen(false)
            toast.success("Packages mapped", {
              description: `Successfully mapped ${selectedPackageIds.size} packages.`
            })
            setSelectedPackageIds(new Set())
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <AdjustPackageDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          packageItem={packages.find(p => p.id === effectiveSelectedPackageId) || null}
          shopId={shopId}
          onAdjustComplete={() => {
            setAdjustOpen(false)
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <BulkAdjustPackagesDialog
          open={bulkAdjustOpen}
          onOpenChange={setBulkAdjustOpen}
          packages={packages.filter(p => selectedPackageIds.has(p.id))}
          shopId={shopId}
          onAdjustComplete={() => {
            setBulkAdjustOpen(false)
            toast.success("Quantities adjusted", {
              description: `Successfully adjusted ${selectedPackageIds.size} packages.`
            })
            setSelectedPackageIds(new Set())
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <MovePackageDialog
          open={moveOpen}
          onOpenChange={setMoveOpen}
          packageItem={packages.find(p => p.id === Array.from(selectedPackageIds)[0]) || null}
          shopId={shopId}
          onMoveComplete={() => {
            setMoveOpen(false)
            toast.success("Packages moved", {
              description: `Successfully moved ${selectedPackageIds.size} packages to new location.`
            })
            setSelectedPackageIds(new Set())
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <FinishPackageDialog
          open={finishOpen}
          onOpenChange={(open) => {
            setFinishOpen(open)
            if (!open) setActionPackage(null)
          }}
          packageItem={actionPackage || packages.find(p => p.id === Array.from(selectedPackageIds)[0]) || null}
          onFinishComplete={() => {
            setFinishOpen(false)
            setActionPackage(null)
            toast.success("Package finished", {
              description: `Successfully marked package as finished.`
            })
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>
      <React.Suspense fallback={null}>
        <BulkFinishPackagesDialog
          open={bulkFinishOpen}
          onOpenChange={(open) => {
            setBulkFinishOpen(open)
            if (!open) setSelectedPackageIds(new Set())
          }}
          packages={packages.filter(p => selectedPackageIds.has(p.id))}
          onFinishComplete={() => {
            setBulkFinishOpen(false)
            setSelectedPackageIds(new Set())
            queryClient.invalidateQueries({ queryKey: queryKeys.packages(shopId!) })
          }}
        />
      </React.Suspense>

      <FormDialog
        open={bulkHoldOpen}
        onOpenChange={(v: boolean) => {
          setBulkHoldOpen(v)
          if (!v) setBulkHoldReason('')
        }}
        title={actionPackage ? 'Place Local Hold' : 'Place Bulk Hold'}
        description={
          actionPackage
            ? `Place a local hold on package ${actionPackage.label.substring(0, 10)}... A reason is required.`
            : `Place a local hold on ${selectedPackageIds.size} selected package${selectedPackageIds.size !== 1 ? 's' : ''}. A reason is required.`
        }
        onConfirm={() => {
          const ids = actionPackage
            ? [actionPackage.id]
            : Array.from(selectedPackageIds)
          bulkHoldMutation.mutate({ ids, reason: bulkHoldReason.trim() })
        }}
        onCancel={() => {
          setBulkHoldOpen(false)
          setBulkHoldReason('')
          setActionPackage(null)
        }}
        confirmLabel={
          bulkHoldMutation.isPending ? 'Placing Hold...' : 'Place Hold'
        }
        isPending={bulkHoldMutation.isPending}
        isConfirmDisabled={!bulkHoldReason.trim()}
        size="slim"
      >
        <div className="space-y-2">
          <Label htmlFor="bulk-hold-reason">Reason</Label>
          <Textarea
            id="bulk-hold-reason"
            placeholder="Enter reason for hold..."
            value={bulkHoldReason}
            onChange={(e) => setBulkHoldReason(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
      </FormDialog>

      <FormDialog
        open={bulkReleaseOpen}
        onOpenChange={(v: boolean) => {
          setBulkReleaseOpen(v)
          if (!v) setBulkReleaseNote('')
        }}
        title={actionPackage ? 'Release Local Hold' : 'Release Bulk Hold'}
        description={
          actionPackage
            ? `Release hold on package ${actionPackage.label.substring(0, 10)}...`
            : `Release hold on ${selectedPackageIds.size} selected package${selectedPackageIds.size !== 1 ? 's' : ''}.`
        }
        onConfirm={() => {
          const ids = actionPackage
            ? [actionPackage.id]
            : Array.from(selectedPackageIds)
          bulkReleaseHoldMutation.mutate({
            ids,
            note: bulkReleaseNote.trim() || undefined,
          })
        }}
        onCancel={() => {
          setBulkReleaseOpen(false)
          setBulkReleaseNote('')
          setActionPackage(null)
        }}
        confirmLabel={
          bulkReleaseHoldMutation.isPending ? 'Releasing...' : 'Release Hold'
        }
        confirmVariant="destructive"
        isPending={bulkReleaseHoldMutation.isPending}
        size="slim"
      >
        <div className="space-y-2">
          <Label htmlFor="bulk-release-note">Note (optional)</Label>
          <Textarea
            id="bulk-release-note"
            placeholder="Optional note for releasing hold..."
            value={bulkReleaseNote}
            onChange={(e) => setBulkReleaseNote(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
      </FormDialog>

      <DeleteConfirmationDialog
        open={isUnmapDialogOpen}
        onOpenChange={setIsUnmapDialogOpen}
        title="Unmap Selected Packages?"
        description={
          <>
            This will remove the mapping between{' '}
            <span className="font-medium text-foreground">
              {selectedPackageIds.size} package{selectedPackageIds.size !== 1 ? 's' : ''}
            </span>{' '}
            and their respective Products/SaleConfigs. The packages will still exist in Metrc.
          </>
        }
        onConfirm={() => {
          unmapMutation.mutate(Array.from(selectedPackageIds))
          setIsUnmapDialogOpen(false)
        }}
        confirmLabel="Unmap Packages"
        isPending={unmapMutation.isPending}
      />
    </div>
  )
}
